import { z } from "zod";
import { IsoDateTime } from "../common.ts";
import { Cursor } from "./common.ts";

// `GET/PUT /me/preferences` (column preferences per console user and screen) and
// `GET /me/notifications` (in-app notifications, e.g. a background export is ready). Authenticated,
// no permission code; always scoped to the session's console user.

export const PREFERENCE_SCREENS = [
  "users",
  "devices",
  "user_transactions",
  "device_transactions",
  "ip_transactions",
  "transaction_cards",
  "blocklist",
] as const;
export const PreferenceScreen = z.enum(PREFERENCE_SCREENS).meta({ id: "PreferenceScreen" });
export type PreferenceScreen = z.infer<typeof PreferenceScreen>;

export const MAX_PREFERENCE_COLUMNS = 40;
const ColumnId = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/);

export const PreferencesResponseV1 = z
  .object({
    /** Visible columns per screen, in display order; a screen absent here uses its defaults. */
    columns: z.partialRecord(PreferenceScreen, z.array(ColumnId).max(MAX_PREFERENCE_COLUMNS)),
  })
  .strict()
  .meta({ id: "PreferencesResponseV1" });
export type PreferencesResponseV1 = z.infer<typeof PreferencesResponseV1>;

export const UpdatePreferencesRequestV1 = z
  .object({
    screen: PreferenceScreen,
    columns: z
      .array(ColumnId)
      .max(MAX_PREFERENCE_COLUMNS)
      .refine((cols) => new Set(cols).size === cols.length, { message: "duplicate column" }),
  })
  .strict()
  .meta({ id: "UpdatePreferencesRequestV1" });
export type UpdatePreferencesRequestV1 = z.infer<typeof UpdatePreferencesRequestV1>;

export const NOTIFICATION_KINDS = ["export_ready", "export_failed"] as const;

export const NotificationsQueryV1 = z
  .object({
    cursor: Cursor.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict()
  .meta({ id: "NotificationsQueryV1" });
export type NotificationsQueryV1 = z.infer<typeof NotificationsQueryV1>;

export const NotificationV1 = z
  .object({
    id: z.uuid(),
    kind: z.enum(NOTIFICATION_KINDS),
    /** The export the notification is about (exports: task group 10). */
    export_id: z.uuid().nullable(),
    created_at: IsoDateTime,
    read_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "NotificationV1" });
export type NotificationV1 = z.infer<typeof NotificationV1>;

export const NotificationsResponseV1 = z
  .object({
    items: z.array(NotificationV1),
    unread: z.int().nonnegative(),
    next_cursor: Cursor.nullable(),
  })
  .strict()
  .meta({ id: "NotificationsResponseV1" });
export type NotificationsResponseV1 = z.infer<typeof NotificationsResponseV1>;
